// Lib
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { compose } from '../../node_module_clones/recompose';
import shallowEqual from 'shallowequal';
import ResizeObserver from 'resize-observer-polyfill';
import { debounce, first } from 'lodash';

// Utils
import measurementsConnect from '../components/measurementsStore/measurementsConnect';
import { getCanvasElementMeasurementsBoundingRect } from '../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import usePrevious from '../utils/react/usePrevious';
import measurementsRegistry from '../components/measurementsStore/measurementsRegistry';
import useDebouncedCallback from '../utils/react/useDebouncedCallback';
import { calculateNextCanvasSize, getMinimumCanvasDocumentSize, EMPTY_CANVAS_SIZE } from './utils/canvasSizeUtils';

// Selectors
import { getCanvasSizePojo } from './store/canvasSizeSelector';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { getCurrentBoardInboxState } from '../element/board/boardSelector';

// Actions
import { setCanvasSize } from './store/canvasActions';
import { getIsAnnotationModeEnabled } from '../element/drawing/annotation/store/annotationSelector';

// Types
import { InboxState } from '../../common/boards/boardConstants';

/**
 * If the unsorted notes is open when going into the drawing editor mode, we need to add space
 * to the canvas-document so that it fills in the space left by the removal of the unsorted notes.
 */
// FIXME-ZOOM - revisit this, it should probably behave the same, but might be weird when zoomed out?
const getAnnotationEditorCanvasSize = (canvasSize, gridSize) => ({
    ...canvasSize,
    width: canvasSize.width + 38 * gridSize,
});

const mapMeasurementsStateToProps = (state) => ({
    canvasElementsBoundingRect: getCanvasElementMeasurementsBoundingRect(state),
});

const mapStateToProps = createSelector(
    getCanvasSizePojo,
    getIsAnnotationModeEnabled,
    getCurrentBoardInboxState,
    (canvasSize, isAnnotationModeEnabled, currentBoardInboxState) => ({
        canvasSize,
        isAnnotationModeEnabled,
        isCurrentBoardInboxOpen: currentBoardInboxState === InboxState.OPEN,
    }),
);

const mapDispatchToProps = (dispatch) => ({
    dispatchSetCanvasSize: (canvasSize) => dispatch(setCanvasSize({ canvasSize })),
});

export default (DecoratedComponent) => {
    const enhance = compose(
        measurementsConnect(mapMeasurementsStateToProps),
        connect(mapStateToProps, mapDispatchToProps),
    );

    const canvasSizeDecorator = (props) => {
        const {
            canvasElementIds,
            isAnnotationModeEnabled,
            canvasSize,
            gridSize,
            currentBoardId,
            dispatchSetCanvasSize,
            zoomScale,
        } = props;

        const { canvasElementsBoundingRect, isCurrentBoardInboxOpen, ...rest } = props;

        const platformDetails = useSelector(getPlatformDetailsSelector);

        const renderedCanvasSizeRef = useRef(EMPTY_CANVAS_SIZE);
        const canvasViewportSizeRef = useRef(EMPTY_CANVAS_SIZE);
        const mounted = useRef(false);

        const prevCanvasSize = usePrevious(canvasSize, EMPTY_CANVAS_SIZE);

        // If the canvas size grows externally to this decorator, ensure the renderedCanvasSizeRef variable updates
        // to the new size
        useEffect(() => {
            renderedCanvasSizeRef.current = {
                width: Math.max(canvasViewportSizeRef.current.width, canvasSize.width),
                height: Math.max(canvasViewportSizeRef.current.height, canvasSize.height),
            };
        }, [canvasSize]);

        const debouncedCalculateCanvasSize = useDebouncedCallback(
            () => {
                if (!mounted.current) return;

                const nextCanvasSize = calculateNextCanvasSize(
                    renderedCanvasSizeRef.current,
                    canvasElementsBoundingRect,
                    gridSize,
                    canvasElementIds,
                    zoomScale,
                    platformDetails,
                );

                // Update canvas if the canvas size has expanded, otherwise leave as is to avoid unnecessary re-renders
                if (!shallowEqual(prevCanvasSize, nextCanvasSize)) {
                    renderedCanvasSizeRef.current = nextCanvasSize;
                    dispatchSetCanvasSize(nextCanvasSize);
                }
            },
            100,
            [canvasElementsBoundingRect, gridSize, canvasElementIds, zoomScale, platformDetails],
        );

        useEffect(() => {
            mounted.current = true;

            return () => {
                mounted.current = false;
            };
        }, []);

        // On a change in current board, reset size to be size of canvas viewport
        useEffect(() => {
            const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();

            if (!canvasViewportNode) return;

            const canvasContainerRect = canvasViewportNode.getBoundingClientRect();

            renderedCanvasSizeRef.current = {
                width: canvasContainerRect.width,
                height: canvasContainerRect.height,
            };
            canvasViewportSizeRef.current = renderedCanvasSizeRef.current;
        }, [currentBoardId]);

        // On a measurement change, recalculate the canvas size based on its content and set canvas size in redux state
        useEffect(() => {
            debouncedCalculateCanvasSize();
        }, [canvasElementsBoundingRect, gridSize, platformDetails]);

        // Ensure that changes in the canvas viewport size (either due to the browser window growing
        // or the unsorted notes closing) that cause the canvas document size to grow (via CSS)
        // will update the store appropriately with the new canvas document size.
        useEffect(() => {
            const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();

            if (!canvasViewportNode) return;

            const debouncedOnCanvasViewportResize = debounce((entries) => {
                const canvasViewportObserverEntry = first(entries);

                if (!canvasViewportObserverEntry) return;

                const newCanvasViewportRect = canvasViewportObserverEntry.contentRect;

                canvasViewportSizeRef.current = {
                    width: newCanvasViewportRect.width,
                    height: newCanvasViewportRect.height,
                };

                const nextCanvasDocumentSize = getMinimumCanvasDocumentSize(
                    renderedCanvasSizeRef.current,
                    newCanvasViewportRect,
                );

                if (nextCanvasDocumentSize && nextCanvasDocumentSize !== renderedCanvasSizeRef.current) {
                    dispatchSetCanvasSize(nextCanvasDocumentSize);
                    renderedCanvasSizeRef.current = nextCanvasDocumentSize;
                }
            }, 100);

            const resizeObserver = new ResizeObserver(debouncedOnCanvasViewportResize);
            resizeObserver.observe(canvasViewportNode);

            return () => {
                resizeObserver?.disconnect();
            };
        }, []);

        const currentCanvasSize =
            isAnnotationModeEnabled && isCurrentBoardInboxOpen
                ? getAnnotationEditorCanvasSize(canvasSize, gridSize)
                : canvasSize;

        return <DecoratedComponent {...rest} canvasSize={currentCanvasSize} />;
    };

    canvasSizeDecorator.propTypes = {
        canvasElementIds: PropTypes.array,
        canvasElementsBoundingRect: PropTypes.object,
        currentBoardId: PropTypes.string,

        canvasSize: PropTypes.object,
        gridSize: PropTypes.number,
        dispatchSetCanvasSize: PropTypes.func,

        isAnnotationModeEnabled: PropTypes.bool,
        isCurrentBoardInboxOpen: PropTypes.bool,
    };

    return enhance(canvasSizeDecorator);
};
